import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "app/components/ui/dialog";

interface Props {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

const features = [
  {
    icon: "/assets/data-outline.svg",
    altText: "Data",
    title: "Access to 3 major networks that switch automatically",
  },
  {
    icon: "/assets/shield-outline.svg",
    altText: "Shield",
    title: "Up to $5M identity theft protection",
  },
  {
    icon: "/assets/globe-roaming.svg",
    altText: "Globe",
    title: "Free roaming in 80+ countries",
  },
  {
    icon: "/assets/call-calling.svg",
    altText: "Calling",
    title: "Keep your current phone number",
  },
];

export const IMEICheckSuccessDialog = ({
  showDialog,
  setShowDialog,
}: Props) => {
  const navigate = useNavigate();
  const [showTopShadow, setShowTopShadow] = useState<boolean>(false);
  const [showBottomShadow, setShowBottomShadow] = useState<boolean>(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const checkScroll = useCallback(() => {
    if (scrollAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollAreaRef.current;
      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
      const hasScrollableContent = scrollHeight > clientHeight;

      setShowTopShadow(!isAtTop && hasScrollableContent);
      setShowBottomShadow(!isAtBottom && hasScrollableContent);
    }
  }, []);

  // Check scroll state on mount, resize, and when content dimensions change
  useEffect(() => {
    // Delay check to allow dialog open animation to complete
    const rafId = requestAnimationFrame(() => {
      checkScroll();
    });
    const timerId = setTimeout(checkScroll, 300);

    window.addEventListener("resize", checkScroll);

    let resizeObserver: ResizeObserver | undefined;
    if (scrollAreaRef.current) {
      resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(scrollAreaRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
      window.removeEventListener("resize", checkScroll);
      resizeObserver?.disconnect();
    };
  }, [checkScroll, showDialog]);

  const handleStartForOneDollar = () => {
    navigate("/network-plan");
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent
        className="reltive top-1/2 gap-0 bg-white w-[95%] sm:max-w-[36.8125rem]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogClose className="absolute z-10 top-4 right-4 rounded-full md:top-5 md:right-5">
          <img
            src="/assets/close-circle.webp"
            className="flex-shrink-0 w-5 h-5 rounded-full opacity-90 md:w-6 md:h-6 hover:opacity-100"
            alt="Close"
          />
        </DialogClose>
        <DialogHeader
          className={`relative z-[1] p-4 sm:p-5 lg:p-6 2xl:p-8 bg-transparent text-left pb-3 sm:pb-4 lg:pb-5 2xl:pb-[1.44rem] transition-all duration-300 ease-in-out ${
            showTopShadow ? "shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]" : ""
          }`}
        >
          <div className="flex justify-center items-center bg-[#FFFAF6] size-[3.625rem] sm:size-16 lg:size-[4.375rem] rounded-xl md:rounded-2xl p-2.5 mb-2.5 md:mb-3">
            <img
              src="/assets/success-emoji.svg"
              alt="Success Emoji"
              className="size-8 sm:size-9 lg:size-10"
            />
          </div>
          <DialogTitle className="capitalize font-mobile text-black text-xl sm:text-[1.375rem] lg:text-2xl 2xl:text-[1.75rem] font-bold leading-6 sm:leading-7 mb-2.5 sm:mb-3">
            Great news! Your phone works with VLE Mobile!
          </DialogTitle>
          <DialogDescription className="text-[#808080] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-4 sm:leading-5 lg:leading-6 2xl:leading-[1.5625rem]">
            Your phone is ready to go – it's compatible with our network and all
            our amazing features.
          </DialogDescription>
        </DialogHeader>
        <div
          ref={scrollAreaRef}
          onScroll={checkScroll}
          className="max-h-[calc(90vh-25rem)] px-4 sm:px-5 lg:px-6 2xl:px-8 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          <ul className="flex flex-col gap-4 md:gap-[1.125rem] mb-5 sm:mb-6 md:mb-7 lg:mb-[1.875rem]">
            {features.map((feature, index) => (
              <li key={index} className="flex gap-2.5 md:gap-3 items-center">
                <div className="shrink-0 flex-grow-0 flex justify-center items-center bg-[#F5F5F5] size-9 md:size-10 p-1.5 md:p-2 rounded-lg">
                  <img
                    src={feature.icon}
                    alt={feature.altText}
                    className="size-full"
                  />
                </div>
                <h4 className="font-mobile text-black text-sm sm:text-[0.925rem] lg:text-base font-semibold leading-[1.0625rem] sm:leading-5 lg:leading-6">
                  {feature.title}
                </h4>
              </li>
            ))}
          </ul>
          <div
            className="rounded-xl text-center p-3 sm:p-3.5 lg:px-4 lg:pt-[1.19rem] lg:pb-3.5"
            style={{
              background:
                "linear-gradient(94deg, rgba(255, 173, 138, 0.1) 0%, rgba(253, 253, 253, 0.8) 70%, #FFF5ED 100%)",
            }}
            // style={{
            //   background: "linear-gradient(94deg, rgba(255, 173, 138, 0.00) -18.1%, rgba(253, 253, 253, 0.58) 39.14%, #FFF5ED 100%)",
            // }}
          >
            <h5 className="max-w-[24.3125rem] text-sm sm:text-[0.925rem] lg:text-base font-bold sm:font-semibold leading-[1.125rem] sm:leading-5 lg:leading-6 text-black mx-auto">
              {/* Try VLE Mobile for just $1 your first month */}
              Get Started with VLE Mobile for first month
            </h5>
            <span className="inline-block text-black text-xs sm:text-[0.825rem] md:text-sm font-normal sm:font-medium leading-[1.125rem] sm:leading-5 lg:leading-6">
              {/* That's basically free! No contracts, no surprises. */}
              That's basically great! No contracts, no surprises.
            </span>
          </div>
        </div>
        <div
          className={`relative z-[1] p-4 sm:p-5 lg:p-6 2xl:p-8 [@media(max-width:400px)]:pt-3.5 pt-4 sm:pt-[1.125rem] lg:pt-[1.125rem] 2xl:pt-[1.125rem] transition-all duration-300 ease-in-out ${
            showBottomShadow ? "shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" : ""
          }`}
        >
          <Button
            type="button"
            variant="primary"
            className="h-11 sm:h-12 md:h-[3.375rem]"
            onClick={handleStartForOneDollar}
          >
            {/* Start for $1 First Month -&gt; */}
            Get Started First Month -&gt;
          </Button>
          <p className="text-center text-[#808080] text-xs lg:text-[0.825rem] font-medium leading-[1.125rem] sm:leading-5 [@media(max-width:400px)]:mt-3.5 mt-4 md:mt-[1.12rem]">
            Takes just 5 minutes to activate and you can keep your phone number
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
